/**
 * The server-side half of the core invariant.
 *
 * Zod `.strict()` on every callable schema is the primary defence — it rejects
 * any key the contract does not name. This module is the second, independent
 * layer: a recursive scan that refuses payloads carrying a *shape* of key that
 * could only exist if secret-bearing client state leaked into a request.
 *
 * It runs before validation, so it also catches keys smuggled inside values
 * that a laxer schema (or a future edit) might have let through.
 */

/** Keys that are forbidden when the normalised key matches exactly. */
export const FORBIDDEN_EXACT = [
  'name',
  'label',
  'alias',
  'location',
  'place',
  'medium',
  'balance',
  'amount',
  'seed',
  'phrase',
  'words',
  'notes',
  'note',
  'memo',
] as const;

/** Keys that are forbidden when the normalised key starts with one of these. */
export const FORBIDDEN_PREFIX = [
  'hint',
  'question',
  'answer',
  'displayname',
  'storagemedium',
  'wallettype',
  'wallet',
  'exactlocation',
  'locationhint',
  'mnemonic',
  'seedphrase',
  'recoveryphrase',
  'passphrase',
  'vaultkey',
  'masterkey',
  'secret',
  'privatekey',
  'entropy',
  'xprv',
  'derivationpath',
] as const;

/** Keys that are forbidden when the normalised key contains one of these. */
export const FORBIDDEN_SUBSTRING = [
  'mnemonic',
  'seedphrase',
  'privatekey',
  'passphrase',
  'wordlist',
  'plaintext',
  'cleartext',
] as const;

/**
 * Keys that survive the rules above because the design explicitly permits them.
 * `hintDepthUsed` is a *count*, not content — it is the one thing about hints
 * the server is allowed to know.
 */
export const FORBIDDEN_EXCEPTIONS = new Set(['hintdepthused', 'hintsused', 'maxhintdepth']);

/** Deepest object nesting we will walk before refusing outright. */
const MAX_DEPTH = 8;
/** Most keys we will inspect before refusing outright. */
const MAX_NODES = 5000;

/** Lowercase and strip everything that is not a letter or digit. */
export function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True when this single key name is one the server must never accept. */
export function isForbiddenKey(key: string): boolean {
  const k = normaliseKey(key);
  if (k === '') return false;
  if (FORBIDDEN_EXCEPTIONS.has(k)) return false;
  if ((FORBIDDEN_EXACT as readonly string[]).includes(k)) return true;
  if (FORBIDDEN_PREFIX.some((p) => k.startsWith(p))) return true;
  if (FORBIDDEN_SUBSTRING.some((s) => k.includes(s))) return true;
  return false;
}

export type ForbiddenScanResult =
  | { ok: true }
  | { ok: false; reason: 'forbidden_key'; path: string }
  | { ok: false; reason: 'too_deep' | 'too_large'; path: string };

/**
 * Walk an arbitrary callable payload and report the first forbidden key.
 *
 * Reports only the *path*, never the value — this result is logged, and logging
 * a value would be the very leak the scan exists to prevent.
 */
export function scanForForbiddenKeys(input: unknown): ForbiddenScanResult {
  let nodes = 0;

  function walk(value: unknown, path: string, depth: number): ForbiddenScanResult {
    if (value === null || typeof value !== 'object') return { ok: true };
    if (depth > MAX_DEPTH) return { ok: false, reason: 'too_deep', path };
    if (++nodes > MAX_NODES) return { ok: false, reason: 'too_large', path };

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const r = walk(value[i], `${path}[${i}]`, depth + 1);
        if (!r.ok) return r;
      }
      return { ok: true };
    }

    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      const childPath = path === '' ? key : `${path}.${key}`;
      if (isForbiddenKey(key)) {
        return { ok: false, reason: 'forbidden_key', path: childPath };
      }
      const r = walk(child, childPath, depth + 1);
      if (!r.ok) return r;
    }
    return { ok: true };
  }

  return walk(input, '', 0);
}
